/**
 * API Service for communicating with BARCODE-DEMO backend
 * This is the foundation for Action 1 - establishing backend communication
 */

// Runtime configurable API base URL
let _apiBaseUrl = 'http://localhost:8001';

/**
 * Set the API base URL at runtime
 */
export function setApiBaseUrl(url: string): void {
  _apiBaseUrl = url;
  console.log('API service configured with URL:', url);
}

/**
 * Get the current API base URL
 */
export function getApiBaseUrl(): string {
  return _apiBaseUrl;
}

export class ApiService {
  /**
   * Test the connection to the backend API
   */
  static async testConnection(): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await fetch(`${_apiBaseUrl}/api/test-connection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('Connection test failed:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Check the health of the backend API
   */
  static async checkHealth(): Promise<{ healthy: boolean; data?: any }> {
    try {
      const response = await fetch(`${_apiBaseUrl}/api/health`);
      const data = await response.json();
      return { healthy: data.status === 'healthy', data };
    } catch (error) {
      console.error('Health check failed:', error);
      return { healthy: false };
    }
  }

  /**
   * Get camera preview frame
   */
  static async getCameraPreview(): Promise<{ 
    frame?: string; 
    width?: number;
    height?: number;
    error?: string 
  }> {
    try {
      const response = await fetch(`${_apiBaseUrl}/api/camera/preview`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        return { error: data.error };
      }
      
      return {
        frame: data.frame,
        width: data.width,
        height: data.height
      };
    } catch (error) {
      console.error('Failed to get camera preview:', error);
      return { 
        error: error instanceof Error ? error.message : 'Failed to get camera preview' 
      };
    }
  }

  /**
   * Trigger product detection
   */
  static async detectProduct(model?: string): Promise<{
    success: boolean;
    product_name?: string;
    barcode?: string;
    detection_method?: string;
    confidence?: number;
    brand?: string;
    weight_valid?: boolean;
    error?: string;
    detection_time?: number;
    details?: any;
  }> {
    try {
      const url = model 
        ? `${_apiBaseUrl}/api/detect?model=${encodeURIComponent(model)}`
        : `${_apiBaseUrl}/api/detect`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success && data.error) {
        return { 
          success: false,
          error: data.error 
        };
      }
      
      return data;
    } catch (error) {
      console.error('Product detection failed:', error);
      return { 
        success: false,
        error: error instanceof Error ? error.message : 'Detection failed' 
      };
    }
  }
}

// Export a singleton instance for convenience
export default ApiService;
